import { useState } from 'react';
import { motion } from 'framer-motion';
import { Send, Mail, MessageSquare, Users, Heart } from 'lucide-react';
import { useSearchParams } from 'react-router-dom';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const fadeUp = {
  initial: { opacity: 0, y: 30 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true, margin: '-80px' },
  transition: { duration: 0.8, ease: [0.22, 1, 0.36, 1] },
};

export default function ContactPage() {
  const [searchParams] = useSearchParams();
  const initialInquiry = searchParams.get('inquiry') || 'general';
  const [form, setForm] = useState({ name: '', email: '', message: '', inquiry_type: initialInquiry });
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(false);

  const handleChange = (e) => {
    setForm({ ...form, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setStatus(null);

    try {
      const res = await fetch(`${BACKEND_URL}/api/contact`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(form),
      });

      if (res.ok) {
        setStatus('success');
        setForm({ name: '', email: '', message: '', inquiry_type: 'general' });
      } else {
        setStatus('error');
      }
    } catch {
      setStatus('error');
    } finally {
      setLoading(false);
    }
  };

  const inquiryTypes = [
    { value: 'general', label: 'General Inquiry' },
    { value: 'collaboration', label: 'Collaboration' },
    { value: 'speaking', label: 'Speaking' },
    { value: 'bulk_orders', label: 'Bulk Orders' },
    { value: 'partnership', label: 'Partnership' },
    { value: 'privacy_policy', label: 'Privacy Policy Inquiry' },
    { value: 'terms_of_service', label: 'Terms of Service Inquiry' },
    { value: 'shipping_policy', label: 'Shipping Policy Inquiry' },
    { value: 'return_policy', label: 'Return & Refund Policy Inquiry' },
    { value: 'other', label: 'Other' },
  ];

  return (
    <div data-testid="contact-page">
      <section className="py-20 md:py-32 bg-[#FDFBF7] min-h-[80vh]">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 md:gap-24">
            {/* Left - Message */}
            <motion.div {...fadeUp}>
              <p className="font-sans text-xs tracking-[0.3em] uppercase text-[#A67A5B] mb-4">
                Get In Touch
              </p>
              <h1 className="font-serif text-4xl sm:text-5xl font-semibold text-[#2A2421] mb-6 leading-tight">
                Let's Connect
              </h1>
              <p className="font-sans text-base md:text-lg text-[#7A736E] leading-relaxed mb-8 max-w-md">
                Whether you're interested in collaborations, speaking engagements, bulk orders, or partnerships, we'd love to hear from you. Every connection is an opportunity for God to move.
              </p>

              <div className="space-y-6">
                <div className="flex items-start gap-4">
                  <div className="w-10 h-10 rounded-xl bg-[#A67A5B]/10 flex items-center justify-center flex-shrink-0">
                    <Mail className="text-[#A67A5B]" size={18} />
                  </div>
                  <div>
                    <p className="font-sans text-sm font-medium text-[#2A2421] mb-1">Reach Us</p>
                    <p className="font-sans text-sm text-[#7A736E]">Use the form on this page to contact us!</p>
                  </div>
                </div>
                <div className="flex items-start gap-4">
                  <div className="w-10 h-10 rounded-xl bg-[#A67A5B]/10 flex items-center justify-center flex-shrink-0">
                    <Heart className="text-[#A67A5B]" size={18} />
                  </div>
                  <div>
                    <p className="font-sans text-sm font-medium text-[#2A2421] mb-1">Inquiries Welcome</p>
                    <p className="font-sans text-sm text-[#7A736E]">Collaborations, speaking, bulk orders, partnerships</p>
                  </div>
                </div>
                <div className="flex items-start gap-4">
                  <div className="w-10 h-10 rounded-xl bg-[#A67A5B]/10 flex items-center justify-center flex-shrink-0">
                    <Users className="text-[#A67A5B]" size={18} />
                  </div>
                  <div>
                    <p className="font-sans text-sm font-medium text-[#2A2421] mb-3">Follow Us</p>
                    <div className="flex gap-3">
                      <a href="https://instagram.com/ledbyfaithstudios" target="_blank" rel="noopener noreferrer" data-testid="contact-instagram" className="text-sm text-[#A67A5B] hover:text-[#8C654A] font-medium transition-colors">Instagram</a>
                      <a href="https://facebook.com/ledbyfaithstudios" target="_blank" rel="noopener noreferrer" data-testid="contact-facebook" className="text-sm text-[#A67A5B] hover:text-[#8C654A] font-medium transition-colors">Facebook</a>
                      <a href="https://tiktok.com/@ledbyfaithstudios" target="_blank" rel="noopener noreferrer" data-testid="contact-tiktok" className="text-sm text-[#A67A5B] hover:text-[#8C654A] font-medium transition-colors">TikTok</a>
                      <a href="https://pinterest.com/ledbyfaithstudios" target="_blank" rel="noopener noreferrer" data-testid="contact-pinterest" className="text-sm text-[#A67A5B] hover:text-[#8C654A] font-medium transition-colors">Pinterest</a>
                    </div>
                  </div>
                </div>
              </div>
            </motion.div>

            {/* Right - Form */}
            <motion.div {...fadeUp}>
              <form onSubmit={handleSubmit} className="space-y-8" data-testid="contact-form">
                {/* Inquiry Type */}
                <div>
                  <label htmlFor="inquiry_type" className="block font-sans text-xs font-medium tracking-[0.15em] uppercase text-[#7A736E] mb-2">
                    Inquiry Type
                  </label>
                  <select
                    id="inquiry_type"
                    name="inquiry_type"
                    value={form.inquiry_type}
                    onChange={handleChange}
                    data-testid="contact-inquiry-type-select"
                    className="w-full bg-transparent border-b-2 border-[#E8E1D9] focus:border-[#A67A5B] px-0 py-3 outline-none transition-colors duration-300 font-sans text-[#2A2421] appearance-none cursor-pointer"
                    style={{ backgroundImage: 'url("data:image/svg+xml;utf8,<svg xmlns=\'http://www.w3.org/2000/svg\' width=\'12\' height=\'12\' viewBox=\'0 0 12 12\'><path fill=\'%23A67A5B\' d=\'M6 8.5L1.5 4h9z\'/></svg>")', backgroundRepeat: 'no-repeat', backgroundPosition: 'right 4px center', paddingRight: '28px' }}
                  >
                    {inquiryTypes.map((type) => (
                      <option key={type.value} value={type.value} data-testid={`contact-inquiry-option-${type.value}`}>
                        {type.label}
                      </option>
                    ))}
                  </select>
                </div>

                {/* Name */}
                <div>
                  <label htmlFor="name" className="block font-sans text-xs font-medium tracking-[0.15em] uppercase text-[#7A736E] mb-2">
                    Name
                  </label>
                  <input
                    id="name"
                    name="name"
                    type="text"
                    value={form.name}
                    onChange={handleChange}
                    required
                    data-testid="contact-name-input"
                    className="w-full bg-transparent border-b-2 border-[#E8E1D9] focus:border-[#A67A5B] px-0 py-3 outline-none transition-colors duration-300 placeholder:text-[#7A736E]/40 font-sans text-[#2A2421]"
                    placeholder="Your name"
                  />
                </div>

                {/* Email */}
                <div>
                  <label htmlFor="email" className="block font-sans text-xs font-medium tracking-[0.15em] uppercase text-[#7A736E] mb-2">
                    Email
                  </label>
                  <input
                    id="email"
                    name="email"
                    type="email"
                    value={form.email}
                    onChange={handleChange}
                    required
                    data-testid="contact-email-input"
                    className="w-full bg-transparent border-b-2 border-[#E8E1D9] focus:border-[#A67A5B] px-0 py-3 outline-none transition-colors duration-300 placeholder:text-[#7A736E]/40 font-sans text-[#2A2421]"
                    placeholder="your@email.com"
                  />
                </div>

                {/* Message */}
                <div>
                  <label htmlFor="message" className="block font-sans text-xs font-medium tracking-[0.15em] uppercase text-[#7A736E] mb-2">
                    Message
                  </label>
                  <textarea
                    id="message"
                    name="message"
                    value={form.message}
                    onChange={handleChange}
                    required
                    rows={5}
                    data-testid="contact-message-input"
                    className="w-full bg-transparent border-b-2 border-[#E8E1D9] focus:border-[#A67A5B] px-0 py-3 outline-none transition-colors duration-300 placeholder:text-[#7A736E]/40 font-sans text-[#2A2421] resize-none"
                    placeholder="Tell us how we can help..."
                  />
                </div>

                {/* Submit */}
                <button
                  type="submit"
                  disabled={loading}
                  data-testid="contact-submit-btn"
                  className="inline-flex items-center justify-center gap-2 bg-[#A67A5B] text-white px-10 py-4 rounded-full font-medium tracking-wide shadow-md hover:bg-[#8C654A] hover:shadow-lg hover:-translate-y-0.5 transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:translate-y-0"
                >
                  {loading ? (
                    <>
                      <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                      Sending...
                    </>
                  ) : (
                    <>
                      <Send size={18} />
                      Send Message
                    </>
                  )}
                </button>

                {/* Status Messages */}
                {status === 'success' && (
                  <motion.div
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    className="p-4 rounded-xl bg-green-50 border border-green-200"
                    data-testid="contact-success-msg"
                  >
                    <p className="font-sans text-sm text-green-700">
                      Thank you for reaching out to our family. Your inquiry has been received successfully. Someone from Led By Faith Studios will respond within 1&ndash;2 business days.
                    </p>
                  </motion.div>
                )}
                {status === 'error' && (
                  <motion.div
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    className="p-4 rounded-xl bg-red-50 border border-red-200"
                    data-testid="contact-error-msg"
                  >
                    <p className="font-sans text-sm text-red-700">
                      Something went wrong. Please try again in a moment.
                    </p>
                  </motion.div>
                )}
              </form>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Newsletter / Stay Updated Section */}
      <section className="py-16 md:py-24 bg-[#2A2421]" data-testid="newsletter-section">
        <div className="max-w-2xl mx-auto px-6 md:px-12 text-center">
          <motion.div {...fadeUp}>
            <div className="w-12 h-[1px] bg-[#C59B76] mx-auto mb-8" />
            <h2 className="font-serif text-2xl sm:text-3xl font-semibold text-white mb-4">
              Stay Connected
            </h2>
            <p className="font-sans text-sm text-white/50 mb-8 leading-relaxed max-w-lg mx-auto">
              Join the Led By Faith Studios community to receive updates on new books, journals, and faith-filled projects.
            </p>
            <NewsletterForm />
            <div className="w-12 h-[1px] bg-[#C59B76] mx-auto mt-8" />
          </motion.div>
        </div>
      </section>
    </div>
  );
}

function NewsletterForm() {
  const [email, setEmail] = useState('');
  const [nlStatus, setNlStatus] = useState(null);
  const [nlLoading, setNlLoading] = useState(false);

  const handleNewsletterSubmit = async (e) => {
    e.preventDefault();
    setNlLoading(true);
    setNlStatus(null);
    try {
      const res = await fetch(`${BACKEND_URL}/api/newsletter`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email, source: 'footer_contact' }),
      });
      const data = await res.json();
      if (res.ok && data.success) {
        setNlStatus({ type: 'success', message: data.message });
        setEmail('');
      } else {
        setNlStatus({ type: 'error', message: 'Something went wrong. Please try again.' });
      }
    } catch {
      setNlStatus({ type: 'error', message: 'Something went wrong. Please try again.' });
    } finally {
      setNlLoading(false);
    }
  };

  return (
    <div>
      <form onSubmit={handleNewsletterSubmit} className="flex flex-col sm:flex-row gap-3 max-w-md mx-auto" data-testid="newsletter-form">
        <input
          type="email"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          required
          placeholder="Your email address"
          data-testid="newsletter-email-input"
          className="flex-1 bg-white/10 border border-white/20 focus:border-[#C59B76] text-white px-5 py-3 rounded-full outline-none transition-colors duration-300 placeholder:text-white/30 font-sans text-sm"
        />
        <button
          type="submit"
          disabled={nlLoading}
          data-testid="newsletter-submit-btn"
          className="bg-[#C59B76] text-white px-6 py-3 rounded-full font-medium text-sm tracking-wide hover:bg-[#A67A5B] transition-all duration-300 disabled:opacity-50 whitespace-nowrap"
        >
          {nlLoading ? 'Joining...' : 'Join the List'}
        </button>
      </form>
      {nlStatus && (
        <motion.p
          initial={{ opacity: 0, y: 5 }}
          animate={{ opacity: 1, y: 0 }}
          data-testid={`newsletter-${nlStatus.type}-msg`}
          className={`text-xs mt-4 ${nlStatus.type === 'success' ? 'text-[#C59B76]' : 'text-red-400'}`}
        >
          {nlStatus.message}
        </motion.p>
      )}
    </div>
  );
}
